import { getQueryClient } from 'app/get-query-client';
import { __directlySetCSSColorPaletteVariables } from 'DesignSystem2/Theme/Palette';
import type { PaletteName } from 'DesignSystem2/Theme/Palette/types';
import { useAuthStore } from 'lib/stores/authStore';
import {
    resolveSystemPreference,
    selectCurrentColorPalette,
    useColorPaletteOverrideStore,
    type ThemeMode,
    type ThemePreference,
} from 'lib/stores/colorPaletteOverrideStore';
import { getUserPropertiesSnapshot, userPropertiesKeys } from 'queries/userProperties';
import { useAppNavigationStore } from 'stores/zustand/appNavigationStore';
import { getForcedColorModeForPath } from 'utils/forcedColorMode';
import { setThemeCookie } from 'utils/theme-cookie';

const isSystemMode = (pref: ThemePreference | undefined): boolean =>
    !pref || pref === 'system' || pref === 'accessible-system';

function setThemeCookieForPreference(palette: ThemePreference | undefined): void {
    if (isSystemMode(palette)) {
        const systemDark = typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
        setThemeCookie(systemDark ? 'dark' : 'light');
    } else {
        setThemeCookie(palette!.includes('dark') ? 'dark' : 'light');
    }
}

function computeStoredPreference(): ThemePreference | undefined {
    const storedColorPalette = getUserPropertiesSnapshot().colorPalette;
    const localStorageColorPalette =
        typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
            ? localStorage.getItem('kalshi-color-palette')
            : undefined;
    const isCharts = typeof window !== 'undefined' && window.location.pathname.startsWith('/charts');
    const isLoggedIn = useAuthStore.getState().isLoggedIn;
    const storedPalette = isLoggedIn ? (storedColorPalette ?? localStorageColorPalette) : localStorageColorPalette;

    if (storedPalette && !isCharts) {
        return storedPalette as ThemePreference;
    }
    return undefined;
}

const CHARTS_COLOR_SCHEMES: PaletteName[] = ['light', 'dark', 'accessible-light', 'accessible-dark'];

/**
 * The palette a broadcast page asked for via `?color_scheme=`.
 *
 * The bootstrap script in app/layout.tsx already reads this param for /charts and paints
 * the page with it before first paint. This bridge runs afterwards and used to force
 * /charts back to `light` unconditionally, which stripped the `dark` class the script had
 * just set and left the page half-themed: dark background, light-mode text.
 *
 * Deliberately scoped to /charts/broadcast/compute. Every other chart page paints at
 * least one literal (non-flipping) color or does its own dark theming against the
 * forced-light palette, so flipping the palette under them breaks them: /charts/broadcast/kpow
 * hardcodes `bg-core-white`, and the mobile embeds (/charts/external, /charts/forecasts)
 * read color_scheme themselves. Only the compute frame is fully palette-driven.
 */
function broadcastColorScheme(): PaletteName | undefined {
    if (typeof window === 'undefined') return undefined;
    if (!window.location.pathname.startsWith('/charts/broadcast/compute')) return undefined;
    const requested = new URLSearchParams(window.location.search).get('color_scheme');
    return CHARTS_COLOR_SCHEMES.find((scheme) => scheme === requested);
}

function computeUserChosenColorPalette(stored: ThemePreference | undefined): PaletteName {
    if (stored === 'system') return resolveSystemPreference(false);
    if (stored === 'accessible-system') return resolveSystemPreference(true);
    if (stored) return stored;
    const isCharts = typeof window !== 'undefined' && window.location.pathname.startsWith('/charts');
    // Charts pages ignore the user's stored preference (they are embeds / browser sources,
    // so the embedder decides), but a broadcast page must honour an explicit color_scheme.
    if (isCharts) return broadcastColorScheme() ?? 'light';
    return resolveSystemPreference(false);
}

function computeUserChosenThemeMode(stored: ThemePreference | undefined): ThemeMode {
    if (isSystemMode(stored)) return 'system';
    return stored!.includes('dark') ? 'dark' : 'light';
}

function applyForcedColorMode(): void {
    // Read pathname from the URL because the navigation store's `currentPathname`
    // can lag soft navigations (SearchParamsBoundary effect).
    const pathname =
        typeof window !== 'undefined' ? window.location.pathname : useAppNavigationStore.getState().currentPathname;
    const forced = pathname ? getForcedColorModeForPath(pathname) : null;
    __directlySetCSSColorPaletteVariables(forced ?? selectCurrentColorPalette(useColorPaletteOverrideStore.getState()));
}

/**
 * Bridges the React Query `userProperties` cache (server-persisted theme preference),
 * auth state, and the system `prefers-color-scheme` media query into
 * `useColorPaletteOverrideStore` — the single Zustand source of truth for the resolved
 * palette — plus applies the URL-forced-color-mode CSS variables. Attached once per
 * app effects bootstrap (mirrors the old `ColorPaletteStore` constructor +
 * `initForcedColorModeAutorun`).
 *
 * Also owns the palette persistence side effects: whenever the server-persisted
 * `colorPalette` in the query cache changes
 * (initial load, a Settings save's optimistic write, or a server echo), it is mirrored
 * into localStorage (`kalshi-color-palette`, read by the pre-hydration bootstrap
 * script) and the SSR theme cookie.
 */
export function attachColorPaletteBridge(): () => void {
    const recomputeUserChosenPreference = (): void => {
        const stored = computeStoredPreference();
        useColorPaletteOverrideStore
            .getState()
            .actions.userChosenPreferenceResolved(
                computeUserChosenColorPalette(stored),
                computeUserChosenThemeMode(stored),
            );
    };

    // Persist the server-backed palette when it changes. `lastSyncedPalette` starts as
    // the current snapshot (usually undefined pre-load) so the first cache write after
    // login refreshes localStorage + cookie exactly like the old `updateFromVo` did.
    let lastSyncedPalette = getUserPropertiesSnapshot().colorPalette;
    const syncPalettePersistence = (): void => {
        const palette = getUserPropertiesSnapshot().colorPalette;
        if (palette === lastSyncedPalette) return;
        lastSyncedPalette = palette;
        if (palette && typeof localStorage !== 'undefined') {
            localStorage.setItem('kalshi-color-palette', palette);
        }
        setThemeCookieForPreference(palette);
    };

    const handleUserPropertiesCacheChange = (): void => {
        syncPalettePersistence();
        recomputeUserChosenPreference();
    };

    recomputeUserChosenPreference();

    // The server-persisted preference now lives in the React Query cache; watch cache
    // writes (login bootstrap fetch, Settings mutations, invalidation refetches) to keep
    // the resolved palette in sync.
    // Browser-only: on the server getQueryClient() returns per-call clients, so there is
    // nothing meaningful to subscribe to.
    let queryCacheCleanup: (() => void) | null = null;
    if (typeof window !== 'undefined') {
        const unsubscribeQueryCache = getQueryClient()
            .getQueryCache()
            .subscribe((event) => {
                if (event.query.queryKey[0] !== userPropertiesKeys.all[0]) return;
                handleUserPropertiesCacheChange();
            });
        queryCacheCleanup = unsubscribeQueryCache;
    }

    // Auth/system-preference changes don't write the userProperties cache, so they need
    // their own explicit triggers.
    const unsubscribeAuth = useAuthStore.subscribe(recomputeUserChosenPreference);

    let mediaQueryCleanup: (() => void) | null = null;
    if (typeof window !== 'undefined' && window.matchMedia) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (): void => recomputeUserChosenPreference();
        mq.addEventListener('change', handleChange);
        mediaQueryCleanup = () => mq.removeEventListener('change', handleChange);
    }

    // Pathname changes can flip the `/charts` special-case in `computeStoredPreference`/
    // `computeUserChosenColorPalette` (e.g. soft-navigating between a regular page and
    // `/charts/...` under the same mount), so the resolved preference must be
    // recomputed here too, not just re-applied from its previous (now stale) value.
    const handleNavigationChange = (): void => {
        recomputeUserChosenPreference();
        applyForcedColorMode();
    };

    applyForcedColorMode();
    const unsubscribeOverrideStore = useColorPaletteOverrideStore.subscribe(applyForcedColorMode);
    const unsubscribePathname = useAppNavigationStore.subscribe(handleNavigationChange);

    return () => {
        queryCacheCleanup?.();
        unsubscribeAuth();
        mediaQueryCleanup?.();
        unsubscribeOverrideStore();
        unsubscribePathname();
    };
}
